import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import InventoryShell from "./InventoryShell";
import { AlertTriangle, ArrowUpRight, Package, Truck } from "lucide-react";

/**
 * Variance landing — cross-session view of unresolved variances for the
 * user's boutique. Serves as a jump-off to the session that owns each
 * variance (audit close-out) or to the delivery that generated it.
 */

function money(n) {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toFixed(2)}`;
}

export default function VariancesLanding() {
  const [state, setState] = useState({ variances: [], count: 0, boutique_code: "", minor_threshold_usd: 5 });
  const [loading, setLoading] = useState(true);
  const [unresolvedOnly, setUnresolvedOnly] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/inventory/variances`, { params: { unresolved_only: unresolvedOnly } });
        if (!alive) return;
        setState(r.data);
      } catch (e) { /* soft — keep the shell */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [unresolvedOnly]);

  return (
    <InventoryShell title="Variance Review" right={
      <label className="text-xs text-stone-500 flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={unresolvedOnly}
          onChange={(e) => setUnresolvedOnly(e.target.checked)}
          data-testid="variances-unresolved-toggle"
          className="h-3.5 w-3.5 rounded border-stone-300"
        />
        Unresolved only
      </label>
    }>
      {loading ? (
        <div className="text-sm text-stone-400">Loading…</div>
      ) : state.variances.length === 0 ? (
        <div className="bg-white subtle-border rounded-3xl p-10 text-sm text-stone-500 text-center" data-testid="variances-empty">
          {unresolvedOnly ? "No unresolved variances. Enjoy the quiet." : "No variances recorded yet."}
        </div>
      ) : (
        <>
          <div className="text-xs text-stone-500 mb-3">
            {state.variances.length} {unresolvedOnly ? "unresolved" : ""} variance{state.variances.length === 1 ? "" : "s"} · {state.boutique_code}
          </div>
          <ul className="space-y-2" data-testid="variances-list">
            {state.variances.map((v) => {
              const value = Number(v.variance_value_usd || 0);
              const isDelivery = v.origin === "delivery";
              const linkTo = isDelivery
                ? `/operations/inventory/receiving/${v.delivery_id}`
                : `/operations/inventory/audit/${v.audit_session_id}/variances`;
              const OriginIcon = isDelivery ? Truck : Package;
              return (
                <li key={v.id}>
                  <Link
                    to={linkTo}
                    data-testid={`variances-item-${v.id}`}
                    className="flex items-center gap-4 bg-white subtle-border rounded-2xl p-4 hover:bg-stone-50 transition-colors"
                  >
                    <div className={`w-1 self-stretch rounded-full ${value >= 0 ? "bg-sky-300" : "bg-amber-300"}`} />
                    <div className={`w-9 h-9 rounded-2xl bg-stone-50 flex items-center justify-center text-stone-600`}>
                      <OriginIcon size={14} strokeWidth={1.5} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-stone-900">{v.product_display_name}</div>
                      <div className="text-[11px] text-stone-500 mt-0.5">
                        expected {v.expected} · counted {v.counted} · {isDelivery ? "from delivery" : "from audit"}
                        {v.resolution && <> · <span className="text-stone-700">{v.resolution.replace(/_/g, " ")}</span></>}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className={`text-sm font-medium ${value >= 0 ? "text-sky-700" : "text-amber-700"}`}>
                        {money(value)}
                      </div>
                    </div>
                    <ArrowUpRight size={14} className="text-stone-300 shrink-0" />
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </InventoryShell>
  );
}
